import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { FileType } from '@lsq-frontend/remote-services/enterprise/common';

import { ModalService } from '../../../services/modal/modal.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { DownloadService } from '../../../services/api/download.service';

const zoomIncrement = 0.25;
const zoomMin = 1;
const zoomMax = 3;

/**
 *
 * @description
 * Preview images from Cloudinary
 * @export
 * @class LsqImagePreviewComponent
 */
@LifecycleAware()
@Component({
    selector: 'lsq-image-preview',
    template: `
        <div #imagePreview class="image-preview-component">
            <div class="image-border">
                <div
                    #imageFrame
                    class="image-frame"
                    [ngClass]="{ 'is-loading': isLoading }"
                    [style.width.px]="maxSize"
                    [style.height.px]="maxSize"
                >
                    <ng-container *ngIf="!failed">
                        <ng-container *ngIf="isLoading">
                            <lsq-circle-loader>
                                <p
                                    *delayRender="2000"
                                    [style.height]="0"
                                    [style.transform]="'translateY(-0.5rem)'"
                                >
                                    Your {{ imageType }} image is loading. This may take a few
                                    seconds.
                                </p>
                            </lsq-circle-loader>
                        </ng-container>

                        <div class="center-image-container">
                            <img
                                *ngIf="!!imageUrl"
                                class="preview-image"
                                [src]="imageUrl"
                                [ngClass]="{
                                    'is-grabbing': isGrabbing
                                }"
                                [attr.draggable]="false"
                                [style.transform]="translate(xOffset, yOffset)"
                                (load)="handleImageLoad()"
                                (mousedown)="handleMouseDown($event)"
                                (touchstart)="handleMouseDown($event, 100)"
                                (mouseup)="handleGrab(false)"
                                (mouseleave)="handleGrab(false)"
                                (touchend)="handleGrab(false)"
                                (touchcancel)="handleGrab(false)"
                                (mousemove)="handleMouseMove($event)"
                                (touchmove)="handleMouseMove($event)"
                            />
                        </div>
                    </ng-container>

                    <div *ngIf="failed" [style.marginTop]="'3rem'">
                        <p>
                            We were unable to fetch your image at this time. Please try again later.
                        </p>
                    </div>
                </div>
            </div>
            <lsq-icon
                *ngIf="hasImageLoaded && page > 1"
                class="page-arrow-icon left"
                (click)="updatePage(page - 1)"
                [type]="icons.LeftArrow"
                [color]="color.Green"
                [hoverColor]="color.GreenDarker"
            >
            </lsq-icon>
            <lsq-icon
                *ngIf="hasImageLoaded && page < totalPages"
                class="page-arrow-icon right"
                (click)="updatePage(page + 1)"
                [type]="icons.RightArrow"
                [color]="color.Green"
                [hoverColor]="color.GreenDarker"
            >
            </lsq-icon>
            <div *ngIf="hasImageLoaded && filename && !downloadUrl" class="left-position filename">
                {{ filename }}
            </div>
            <div
                *ngIf="hasImageLoaded"
                class="zoom-controls"
                [ngClass]="!!downloadUrl ? 'right-position' : 'center-position'"
            >
                <lsq-icon
                    [ngClass]="{ hidden: zoomLevel <= zoomMin }"
                    class="zoom-out-icon"
                    (click)="zoomOut()"
                    [type]="icons.ZoomOut"
                    [color]="color.Green"
                    [hoverColor]="color.GreenDarker"
                >
                </lsq-icon>
                <span [style.width.rem]="4" class="text-center font-weight-bold">{{
                    zoomLevel | percent
                }}</span>
                <lsq-icon
                    [ngClass]="{ hidden: zoomLevel >= zoomMax }"
                    class="zoom-in-icon"
                    (click)="zoomIn()"
                    [type]="icons.ZoomIn"
                    [color]="color.Green"
                    [hoverColor]="color.GreenDarker"
                >
                </lsq-icon>
            </div>
            <a
                *ngIf="!!downloadUrl"
                class="center-position"
                (click)="download()"
                href="javascript:void(0)"
            >
                <lsq-icon
                    *ngIf="hasImageLoaded"
                    [type]="icons.Export"
                    [color]="color.Green"
                    [hoverColor]="color.GreenDarker"
                >
                </lsq-icon>
            </a>
            <div
                *ngIf="hasImageLoaded && !!totalPages"
                class="page-indicators font-weight-bold"
                [ngClass]="!!downloadUrl ? 'left-position' : 'right-position'"
            >
                {{ page }} / {{ totalPages }}
            </div>
        </div>
    `,
    styleUrls: ['./lsq-image-preview.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqImagePreviewComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input() public imageUrl?: string;
    @Input() public filename?: string;
    @Input() public downloadUrl?: string;
    @Input() public totalPages = 1;
    @Input() public failed?: boolean;
    @Input() public imageType: 'check' | 'invoice';
    @Input() public page = 1;
    @Output() public fetchImage = new EventEmitter<void>();
    @Output() public pageChange = new EventEmitter<number>();
    @ViewChild('imagePreview') public imagePreview: ElementRef;
    @ViewChild('imageFrame') public imageFrame: ElementRef;
    public icons = Icons;
    public color = Color;
    public zoomLevel = 1;
    public isGrabbing = false;
    public hasImageLoaded = false;
    public imageWidth: number;
    public imageHeight: number;
    public zoomMin = zoomMin;
    public zoomMax = zoomMax;
    public isLoading = true;
    public isZoomingIn = false;
    public xOffset = 0;
    public yOffset = 0;
    public maxSize: number;
    public startDragCoords = {
        x: 0,
        y: 0
    };
    public startOffsetCoords = {
        x: 0,
        y: 0
    };

    private _imageLoadSubject = new Subject<void>();
    private _isGrabbingSubject = new Subject<boolean>();
    private _mouseDownTimer: any;

    constructor(
        private _downloadService: DownloadService,
        private _responsiveService: ResponsiveService,
        private _modalService: ModalService,
        private _renderer: Renderer2
    ) {}

    public ngOnInit(): void {
        if (typeof this.totalPages !== 'undefined' && !this.totalPages) {
            console.warn('[LsqImagePreviewComponent] totalPages was passed a falsy value.');
        }

        this._responsiveService.smBelow
            .pipe(
                forLifeOf(this),
                distinctUntilChanged(),
                // Do not emit on resize until `_imageLoadSubject` has emitted at least once.
                withLatestFrom(this._imageLoadSubject)
            )
            .subscribe(() => this.setImageDimensions());

        this._isGrabbingSubject.pipe(forLifeOf(this)).subscribe((isGrabbing) => {
            this.isGrabbing = isGrabbing;

            if (!!this._modalService.modal) {
                if (this.isGrabbing) {
                    this._modalService.blockScrolling();
                } else {
                    this._modalService.unblockScrolling();
                }
            } else {
                if (this.isGrabbing) {
                    this._renderer.addClass(document.body, 'block-scroll');
                } else {
                    this._renderer.removeClass(document.body, 'block-scroll');
                }
            }
        });
    }

    public ngAfterViewInit(): void {
        Utilities.executeAfterRender(() => {
            this.setImageDimensions();
            this.fetchImage.emit();
        });
    }

    public ngOnDestroy(): void {}

    public reset(): void {
        this.isLoading = true;
        this.hasImageLoaded = false;
        this.zoomLevel = 1;
    }

    public updatePage(newPage: number): void {
        if (newPage > 0 && newPage <= this.totalPages) {
            this.page = newPage;
            this.pageChange.emit(this.page);
            this.reset();
            this.fetchImage.emit();
        }
    }

    public zoomOut(): void {
        this.zoomLevel -= zoomIncrement;
        this.isLoading = true;
        this.fetchImage.emit();
    }

    public zoomIn(): void {
        this.zoomLevel += zoomIncrement;
        this.isLoading = true;
        this.isZoomingIn = true;
        this.fetchImage.emit();
    }

    public handleImageLoad(): void {
        this._imageLoadSubject.next();
        this.isLoading = false;
        this.isZoomingIn = false;
        this.hasImageLoaded = true;
        this.setImageDimensions();
    }

    public setImageDimensions(): void {
        const imageFrameElement: HTMLElement = this.imageFrame.nativeElement;
        const imagePreviewElement: HTMLElement = this.imagePreview.nativeElement;
        const imageElement: HTMLElement = imageFrameElement.querySelector('img.preview-image');

        const maxDimension =
            imagePreviewElement.getBoundingClientRect().width -
            +window
                .getComputedStyle(imagePreviewElement)
                .getPropertyValue('padding-left')
                .replace('px', '') -
            +window
                .getComputedStyle(imagePreviewElement)
                .getPropertyValue('padding-right')
                .replace('px', '');

        if (!this.hasImageLoaded) {
            this.maxSize = maxDimension;
        }

        if (!!imageElement) {
            this.imageWidth = imageElement.getBoundingClientRect().width;
            this.imageHeight = imageElement.getBoundingClientRect().height;
        }

        this.correctToBoundaries();
    }

    public translate(xOffset: number, yOffset: number): string {
        return `translate(${xOffset}px, ${yOffset}px)`;
    }

    public handleMouseDown(evt: MouseEvent | TouchEvent, delay?: number): void {
        const { clientX, clientY } = this._getClientXYFromEvent(evt);

        if (typeof delay === 'number') {
            this._mouseDownTimer = setTimeout(
                () => this._handleMouseDownAtPosition(clientX, clientY),
                delay
            );
        } else {
            this._handleMouseDownAtPosition(clientX, clientY);
        }
    }

    public handleMouseMove(evt: MouseEvent | TouchEvent): void {
        if (this.isGrabbing) {
            const { clientX, clientY } = this._getClientXYFromEvent(evt);

            if (this.maxSize < this.imageWidth) {
                this.xOffset = this.startOffsetCoords.x + clientX - this.startDragCoords.x;
            }
            if (this.maxSize < this.imageHeight) {
                this.yOffset = this.startOffsetCoords.y + clientY - this.startDragCoords.y;
            }

            this.correctToBoundaries();
        }
    }

    public handleGrab(isGrabbing = true): void {
        this._isGrabbingSubject.next(isGrabbing);
        if (!isGrabbing) {
            clearTimeout(this._mouseDownTimer);
        }
    }

    public correctToBoundaries(): void {
        if (this.zoomLevel > 1) {
            if (this.xOffset > 0) {
                // Check for a left boundary correction.
                this.xOffset = 0;
            }
            if (this.yOffset > 0) {
                // Check for a top boundary correction.
                this.yOffset = 0;
            }
            if (this.imageFrame && this.imageHeight && this.imageWidth) {
                if (this.xOffset + this.imageWidth < this.maxSize) {
                    // Check for a right boundary correction.
                    if (this.imageWidth < this.maxSize) {
                        this.xOffset = 0;
                    } else {
                        this.xOffset = this.maxSize - this.imageWidth;
                    }
                }
                if (this.yOffset + this.imageHeight < this.maxSize) {
                    // Check for a bottom boundary correction.
                    if (this.imageHeight < this.maxSize) {
                        this.yOffset = 0;
                    } else {
                        this.yOffset = this.maxSize - this.imageHeight;
                    }
                }
            }
        } else {
            // At no zoom, clear the offsets.
            this.xOffset = 0;
            this.yOffset = 0;
        }
    }

    public async download(): Promise<void> {
        return this._downloadService.executeUrl(this.downloadUrl, FileType.Pdf, this.filename);
    }

    // Take either a `MouseEvent` or `TouchEvent` and return the coordinates of the event relative
    // to the window.
    private _getClientXYFromEvent(
        event: MouseEvent | TouchEvent
    ): { clientX: number; clientY: number } {
        let clientX: number;
        let clientY: number;

        if (typeof (event as MouseEvent).clientX !== 'undefined') {
            clientX = (event as MouseEvent).clientX;
            clientY = (event as MouseEvent).clientY;
        } else if ((event as TouchEvent).touches && (event as TouchEvent).touches.length) {
            clientX = (event as TouchEvent).touches[0].clientX;
            clientY = (event as TouchEvent).touches[0].clientY;
        }

        return { clientX, clientY };
    }

    private _handleMouseDownAtPosition(clientX: number, clientY: number): void {
        this.handleGrab(true);
        this.startDragCoords.x = clientX;
        this.startDragCoords.y = clientY;
        this.startOffsetCoords.x = this.xOffset;
        this.startOffsetCoords.y = this.yOffset;
    }
}
